import type { FunctionComponent } from "react";
import {
  Map as MapIcon,
  type SVGIconProps,
  InformationCircle,
  Calendar,
  Car,
  Smileys,
} from "@repo/icons";
import { differenceInMinutes } from "date-fns";
import { cn, TimeSlotSuggestionType } from "@repo/lib";
import { createElement } from "react";
import { AvailableOptionsSelectorItem } from "@/components/available-options-selector";
import {
  CarouselContainer,
  CarouselContent,
  CarouselItem,
  CarouselRoot,
} from "@/components/carousel";
import {
  SectionHeaderContent,
  SectionHeaderIcon,
  SectionHeaderRoot,
  SectionHeaderTitle,
} from "@/components/section-header";

interface Unit {
  id: number;
  name: string;
  acronym: string;
}

export interface Slot {
  unit: Unit;
  start?: Date;
  end?: Date;
  distance?: number;
  timeSlotType?: TimeSlotSuggestionType;
}

interface SecondaryLabel {
  icon: FunctionComponent<SVGIconProps>;
  text: string;
}

interface AppointmentAvailableOptionsSectionProps {
  slots: Slot[];
  headerTitle: string;
  icon: FunctionComponent<SVGIconProps>;
  extraLabel?: string;
  extraLabelIcon?: FunctionComponent<SVGIconProps>;
  selectedUnitName?: string;
  onClickOption: (slot: Slot) => void;
  countLocationsSearchResults?: number;
}

export const AppointmentAvailableOptionsSection = ({
  slots,
  headerTitle,
  icon,
  extraLabel,
  extraLabelIcon,
  selectedUnitName,
  onClickOption,
  countLocationsSearchResults,
}: AppointmentAvailableOptionsSectionProps): JSX.Element => {
  const getDistanceString = (distance: number, locationName: string): string => {
    const distanceValue = distance;
    if (distanceValue >= 1) {
      return `${distanceValue.toFixed(1)}km da unidade ${locationName}`;
    }
    return `${Math.round(distanceValue * 1000)}m da unidade ${locationName}`;
  };

  const getTimeSlotString = (start: Date | undefined, end: Date | undefined): string => {
    const options: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    if (!start || !end) {
      return "Sem horários";
    }
    return `${start.toLocaleTimeString("pt-BR", options)} - ${end.toLocaleTimeString("pt-BR", options)}`;
  };

  const getSecondaryLabels = (slot: Slot): SecondaryLabel[] => {
    const labels: SecondaryLabel[] = [];

    if (slot.distance && slot.distance > 0 && selectedUnitName) {
      labels.push({
        icon: Car,
        text: getDistanceString(slot.distance, selectedUnitName),
      });
    }

    if (
      slot.timeSlotType === TimeSlotSuggestionType.LimitedDurationAppointment &&
      slot.start &&
      slot.end
    ) {
      const minutes = differenceInMinutes(slot.end, slot.start);

      labels.push({
        icon: InformationCircle,
        text: `Limitado a ${minutes}min`,
      });
    }

    if (headerTitle === "Unidades próximas") {
      labels.push({
        icon: MapIcon,
        text: "Alternativa próxima a você",
      });
    }

    if (extraLabel && extraLabelIcon) {
      if (labels.length < 2) {
        labels.push({
          icon: extraLabelIcon,
          text: extraLabel,
        });
      }
    }

    if (headerTitle === "Unidades com agenda aberta") {
      labels.push({
        icon: Calendar,
        text: "Unidade com agenda aberta",
      });
    }

    return labels.slice(0, 2);
  };

  const onClick = (slot: Slot): void => {
    onClickOption(slot);
  };

  if (slots.length === 0) {
    return (
      <>
        <SectionHeaderRoot className="px-4">
          <SectionHeaderContent>
            <SectionHeaderIcon>
              {createElement(icon, { size: 24, className: "fill-secondary-400" })}
            </SectionHeaderIcon>
            <SectionHeaderTitle className="text-secondary-400">
              {headerTitle}
            </SectionHeaderTitle>
          </SectionHeaderContent>
        </SectionHeaderRoot>

        <div className="w-full px-4">
          <AvailableOptionsSelectorItem
            isChecked={false}
            value={0}
            data={{
              timeSlot: "Sem horários",
            }}
            displayName={
              countLocationsSearchResults && countLocationsSearchResults > 0
                ? `${countLocationsSearchResults} unidades buscadas`
                : "Nenhuma unidade próxima"
            }
            className="bg-neutral-50"
          >
            <div className="flex flex-row text-neutral-500 gap-1 items-center">
              <Smileys size={16} className="fill-neutral-500 shrink-0" />
              <small className="font-normal text-sm">
                Não tem horários disponíveis neste dia
              </small>
            </div>
          </AvailableOptionsSelectorItem>
        </div>
      </>
    );
  }
  if (slots.length === 1) {
    const item = slots[0];
    return (
      <>
        <SectionHeaderRoot className="px-4">
          <SectionHeaderContent>
            <SectionHeaderIcon>
              {createElement(icon, { size: 24, className: "fill-secondary-400" })}
            </SectionHeaderIcon>
            <SectionHeaderTitle className="text-secondary-400">
              {headerTitle}
            </SectionHeaderTitle>
          </SectionHeaderContent>
        </SectionHeaderRoot>

        <div className="w-full px-4">
          <AvailableOptionsSelectorItem
            isChecked={false}
            value={item.unit.id}
            onChange={() => onClick(item)}
            data={{
              acronym: item.unit.acronym,
              timeSlot: getTimeSlotString(item.start, item.end),
              timeSlotType: item.timeSlotType,
            }}
            displayName={`Unidade ${item.unit.name}`}
            className={cn({
              "bg-secondary-50":
                item.timeSlotType === TimeSlotSuggestionType.LimitedDurationAppointment,
            })}
          >
            {item.start && item.end ? (
              <>
                {getSecondaryLabels(item).map((label) => (
                  <div
                    key={label.text}
                    className="flex flex-row text-neutral-500 gap-1 items-center"
                  >
                    <label.icon size={16} className="fill-secondary-500 shrink-0" />
                    <small className="font-normal text-sm">{label.text}</small>
                  </div>
                ))}
              </>
            ) : (
              <div className="flex flex-row text-neutral-500 gap-1 items-center">
                <Smileys size={16} className="fill-neutral-500 shrink-0" />
                <small className="font-normal text-sm">
                  Não tem horários disponíveis neste dia
                </small>
              </div>
            )}
          </AvailableOptionsSelectorItem>
        </div>
      </>
    );
  }

  return (
    <>
      <SectionHeaderRoot className="px-4">
        <SectionHeaderContent>
          <SectionHeaderIcon>
            {createElement(icon, { size: 24, className: "fill-secondary-400" })}
          </SectionHeaderIcon>
          <SectionHeaderTitle className="text-secondary-400">
            {headerTitle}
          </SectionHeaderTitle>
        </SectionHeaderContent>
      </SectionHeaderRoot>

      <CarouselRoot align="center">
        <CarouselContainer>
          <CarouselContent>
            {slots.map((item, index) => (
              <CarouselItem key={item.unit.id}>
                <AvailableOptionsSelectorItem
                  isChecked={false}
                  value={item.unit.id}
                  onChange={() => onClick(item)}
                  data={{
                    acronym: item.unit.acronym,
                    timeSlot: getTimeSlotString(item.start, item.end),
                    timeSlotType: item.timeSlotType,
                  }}
                  displayName={`Unidade ${item.unit.name}`}
                  className={cn({
                    "bg-secondary-50":
                      item.timeSlotType ===
                      TimeSlotSuggestionType.LimitedDurationAppointment,
                    "mr-4": index === slots.length - 1,
                    "ml-4": index === 0,
                  })}
                >
                  {getSecondaryLabels(item).map((label, indexLabels) => (
                    <div
                      key={`${label.text} + ${indexLabels + 1}`}
                      className="flex flex-row text-neutral-500 gap-1 items-center"
                    >
                      <label.icon size={16} className="fill-secondary-500 shrink-0" />
                      <small className="font-normal text-sm">{label.text}</small>
                    </div>
                  ))}
                </AvailableOptionsSelectorItem>
              </CarouselItem>
            ))}
          </CarouselContent>
        </CarouselContainer>
      </CarouselRoot>
    </>
  );
};
